import { useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { useTranslation } from "react-i18next"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { faCalendar, faClose, faUmbrellaBeach } from "@fortawesome/free-solid-svg-icons"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import InputField from "../../../components/InputField/InputField"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import GetSpace from "../../../utils/space"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface Props {
  onClose: () => void
}

export const VacationSettingsPopup: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const { handleSaveCompany } = useSaveCompany()

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const [isVacationEnabled, setIsVacationEnabled] = useState(companyMemory.isVacationEnabled)

  const [vacationStartDate, setVacationStartDate] = useState(companyMemory.vacationStartDate?.slice(0, 10) ?? "")

  const [vacationEndDate, setVacationEndDate] = useState(companyMemory.vacationEndDate?.slice(0, 10) ?? "")

  const handleTrySave = async () => {
    companyMemory.isVacationEnabled = isVacationEnabled

    companyMemory.vacationStartDate = isVacationEnabled ? vacationStartDate : ""

    companyMemory.vacationEndDate = isVacationEnabled ? vacationEndDate : ""

    const saved = await handleSaveCompany(companyMemory, onClose)

    if (!saved) {
      return
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("vacation.title")}
              icon={faUmbrellaBeach}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.vacationsettingspopup.1"
                    variant="global"
                    title={t("helpPopup:global.vacationPeriod.title")}
                    description={t("helpPopup:global.vacationPeriod.description")}
                    bullets={t("helpPopup:global.vacationPeriod.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.vacationSettingsPopup.button.onClose"
                  />
                </div>
              }
            />

            <div className="mt-4 space-y-4 p-1">
              <SimpleToggle
                title={t("vacation.enable")}
                value={isVacationEnabled}
                disabled={canEdite}
                onChange={(value) => setIsVacationEnabled(value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.vacationSettingsPopup.toggle.enable"
              />

              {isVacationEnabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField
                    type="date"
                    title={t("vacation.start_title")}
                    value={vacationStartDate}
                    disable={canEdite}
                    icon={faCalendar}
                    placeholder={t("vacation.start_date")}
                    onInvalidMessage={t("vacation.invalid")}
                    onChange={(e) => setVacationStartDate(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.vacationSettingsPopup.input.startDate"
                  />

                  <InputField
                    type="date"
                    title={t("vacation.end_title")}
                    value={vacationEndDate}
                    disable={canEdite}
                    icon={faCalendar}
                    placeholder={t("vacation.end_date")}
                    onInvalidMessage={t("vacation.invalid")}
                    onChange={(e) => setVacationEndDate(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.vacationSettingsPopup.input.endDate"
                  />
                </div>
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <section className={defaultStyle.buttonsController}>
              <CanView permission={Permissions.CanEditeCompany}>
                <Button
                  label={t("buttons:button_save")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  onClick={handleTrySave}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.vacationSettingsPopup.button.save"
                />
              </CanView>

              <GetSpace right={5} />

              <Button
                label={t("buttons:button_register")}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGRegisterColor}
                onClick={handleEnableRegister}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.vacationSettingsPopup.button.register"
              />
            </section>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
